/**
 * The ⋮ actions menu that sits in every table's sticky right-hand column.
 *
 * Andressa's spec replaces the bare trash button with this across the app. The
 * shell — trigger, popover geometry, two-line items — lives here so the menus
 * stay identical; what each table puts inside differs, because what a row IS
 * differs:
 *
 *   - Schedule, Regulatory, Operational: a row is (or joins to) a facility →
 *     `FacilityRowActionsMenu` (Archive / Restore / Delete the facility).
 *     Archiving a facility is a GLOBAL action: it cascades across every tab,
 *     since `Facility.isExcluded` lives in one place (`useFacilities`) that
 *     all tabs read from.
 *   - Loss Runs: a row is a claim → `RowDeleteMenu`, carrying the delete the
 *     trash used to do, plus Archive/Restore of the claim itself via the
 *     `archive` prop (OR-1431 — there's no per-claim exclusion flag in the
 *     backend, so this rides on the claim's own `status`; see
 *     `loss-runs/claim-archive.ts`) AND, independently, Archive/Restore of the
 *     claim's facility via the `facilityArchive` prop. The two are unrelated:
 *     a claim can be archived without its facility being archived, and vice
 *     versa.
 *   - Legal: a row is a litigation marker joined to a facility → `RowDeleteMenu`
 *     with only `facilityArchive` (Restore only, no Archive, no delete — see
 *     `pages/submission/heat-map/heatmap-table.tsx`).
 *
 * On Loss Runs the claim-level halves act on different things, deliberately:
 * delete removes the row (a hard DELETE), claim Archive/Restore leaves it in
 * place with its `status` overwritten, struck through as history and dropped
 * from the FE-derived totals. Facility Archive/Restore is a third, separate
 * thing again — it acts on the shared facility, not the claim row.
 *
 * Built on react-aria-components directly, matching the Acuity year-column menu
 * in `pages/submission/regulatory/index.tsx` (atlas-ui has a nicer `Dropdown`
 * compound but it isn't exported from the package barrel, and its `Item`
 * doesn't do the two-line title + description shape these menus need).
 */
import type { FC, ReactNode, SVGProps } from "react";
import { DotsVertical, Folder, RefreshCcw01, Trash02 } from "@untitledui/icons";
import {
    Button as AriaButton,
    Menu as AriaMenu,
    MenuItem as AriaMenuItem,
    MenuTrigger as AriaMenuTrigger,
    Popover as AriaPopover,
    Separator as AriaSeparator,
} from "react-aria-components";
import { cx } from "@tricura/atlas-ui";

const TRIGGER_CLASS =
    "text-fg-quaternary hover:text-fg-secondary hover:bg-secondary_hover " +
    "focus-visible:ring-focus-ring inline-flex size-7 cursor-pointer items-center justify-center " +
    "rounded outline-none transition duration-100 ease-linear " +
    "focus-visible:ring-2 aria-expanded:bg-secondary_hover " +
    // Always opt back into pointer events: `ARCHIVED_ROW` turns them off across
    // the row, and on a facility table this menu is the only way out of the
    // archived state — a dead ⋮ means the facility can't be restored. Owned
    // here rather than left to each callsite to remember, which is how Schedule
    // shipped with it missing.
    "pointer-events-auto";

/** 240px wide per the Figma spec; the height hugs the content. */
const MENU_CLASS = "bg-primary border-secondary w-60 rounded-lg border p-1 shadow-lg outline-none";

export const ROW_ACTIONS_ITEM_CLASS =
    "group/item flex cursor-pointer flex-col gap-0.5 rounded-md px-2.5 py-2 outline-none " +
    "hover:bg-secondary_hover focus:bg-secondary_hover " +
    "data-disabled:cursor-not-allowed data-disabled:text-fg-disabled";

export const ROW_ACTIONS_SEPARATOR_CLASS = "bg-border-secondary mx-2.5 my-1 h-px";

/** Title + description, so both lines pick up the destructive colour together. */
export function RowActionsMenuItemBody({
    icon: Icon,
    title,
    description,
    destructive,
}: {
    icon: FC<SVGProps<SVGSVGElement>>;
    title: string;
    description: string;
    destructive?: boolean;
}) {
    return (
        <>
            <span
                className={cx(
                    "flex items-center gap-2 text-sm font-semibold",
                    destructive ? "text-error-primary" : "text-secondary",
                )}
            >
                {/* Archive / Restore render a muted grey glyph against the dark
                  * label, per the mockup — so the icon carries its own colour
                  * rather than inheriting the title's. Delete is the exception:
                  * there the red covers icon and label together. */}
                <Icon aria-hidden="true" className={cx("size-4 shrink-0", !destructive && "text-fg-quaternary")} />
                {title}
            </span>
            <span className="text-tertiary pl-6 text-xs font-normal">{description}</span>
        </>
    );
}

interface RowActionsMenuProps {
    /** Goes on both the trigger and the menu, so screen readers can tell rows
     *  apart. Callers pass "Actions for <name>" where they have one. */
    label: string;
    onAction: (key: string) => void;
    /** `AriaMenuItem`s, passed as DIRECT children so react-stately's collection
     *  builder sees them — don't wrap them in fragments or arrays. */
    children: ReactNode;
    /** Extra classes for the trigger button. */
    className?: string;
    /** Custom attribute for E2E testing. */
    "data-autotest"?: string;
}

export function RowActionsMenu({ label, onAction, children, className, "data-autotest": dataAutotest }: RowActionsMenuProps) {
    return (
        <AriaMenuTrigger>
            <AriaButton aria-label={label} data-autotest={dataAutotest} className={cx(TRIGGER_CLASS, className)}>
                <DotsVertical className="size-4" />
            </AriaButton>
            <AriaPopover
                placement="bottom end"
                offset={4}
                className="z-50 entering:animate-in entering:fade-in entering:zoom-in-95"
            >
                <AriaMenu aria-label={label} className={MENU_CLASS} onAction={(key) => onAction(String(key))}>
                    {children}
                </AriaMenu>
            </AriaPopover>
        </AriaMenuTrigger>
    );
}

/** Archive/Restore of the row itself (a claim, on Loss Runs — the only current
 *  caller). Omitted where there's nothing to offer archiving on. */
export interface RowArchiveActions {
    isArchived: boolean;
    onArchive: () => void;
    onRestore: () => void;
}

/** Restore of the row's FACILITY — see `facilityArchive` below. Loss Runs
 *  never initiates a facility archive from this menu (that stays a
 *  Schedule-initiated action, same as Legal), so there's no `onArchive`
 *  here — only ever a Restore, offered when the facility IS archived. */
export interface RowFacilityRestoreAction {
    isArchived: boolean;
    onRestore: () => void;
    /**
     * True when the row's own archive flag (the claim's `status`, on Loss
     * Runs) is ALSO set, independent of the facility — not merely a visual
     * side-effect of the facility being archived. `onRestore` must clear both
     * in this case: the row's `archived` look is `facilityArchived ||
     * claimArchived`, so restoring the facility alone would leave the row
     * struck through with no menu item left to clear it. Swaps in copy that
     * tells the user the claim comes back too.
     */
    alsoRestoresRow?: boolean;
}

interface RowDeleteMenuProps {
    /** e.g. "Delete claim". Names what the row is, not the facility behind it. */
    title: string;
    description: string;
    onDelete: () => void;
    /**
     * Adds Archive/Restore of the ROW itself (a claim, on Loss Runs) above
     * the delete. Hidden whenever `facilityArchive.isArchived` is true — once
     * the facility is archived, the row's own status no longer decides how it
     * renders (see `archived = facilityArchived || claimArchived` in
     * `loss-runs-table.tsx`), so toggling it alone would silently do nothing
     * visible and only "Restore Facility" makes sense.
     */
    archive?: RowArchiveActions;
    /**
     * Restores the row's FACILITY — a GLOBAL action cascading across every
     * tab. Only ever rendered (as "Restore Facility") when the facility is
     * actually archived; omitted otherwise and when the row has no
     * resolvable facility. Mutually exclusive with `archive` — see its note.
     */
    facilityArchive?: RowFacilityRestoreAction;
    /** Row identity for the aria-label, e.g. the claimant's name. */
    rowLabel?: string;
    className?: string;
    /** Custom attribute for E2E testing — applied to the trigger; suffixed
     *  per item (`-archive`/`-restore`/`-restore-facility`/`-delete`). */
    "data-autotest"?: string;
}

/** Stable collection keys for the two Archive/Restore toggles. react-stately
 *  throws "Cannot change the id of an item" if the id at a position changes
 *  while the popover is mounted — which is exactly what acting on either
 *  toggle does — so neither may depend on `isArchived`. */
const TOGGLE_ITEM_ID = "toggle-archive";
const TOGGLE_FACILITY_ARCHIVE_ID = "toggle-facility-archive";

/**
 * The ⋮ for a table whose rows are not facilities.
 *
 * The delete acts on the ROW (a claim, one year's staffing); `archive`, when
 * offered (Loss Runs only, today), ALSO acts on the row itself — the claim's
 * own `status` field stands in for a per-claim exclusion flag the backend
 * doesn't have (OR-1431, `loss-runs/claim-archive.ts`). `facilityArchive` is a
 * DIFFERENT, mutually exclusive control for the row's facility: exactly one of
 * the two ever renders, chosen by the facility's own archived state, so the
 * user is never shown a claim toggle that can't change how the row looks.
 *
 * Delete/Archive copy is the approved facility menu's sentences with the noun
 * swapped — worth confirming with design, which never specified this menu
 * (its mockups draw the ⋮ closed on these two pages).
 */
export function RowDeleteMenu({
    title,
    description,
    onDelete,
    archive,
    facilityArchive,
    rowLabel,
    className,
    "data-autotest": dataAutotest,
}: RowDeleteMenuProps) {
    const label = rowLabel?.trim() ? `Actions for ${rowLabel.trim()}` : "Row actions";
    const facilityIsArchived = facilityArchive?.isArchived === true;
    const showClaimToggle = archive !== undefined && !facilityIsArchived;
    const showFacilityToggle = facilityArchive !== undefined && facilityIsArchived;
    return (
        <RowActionsMenu
            label={label}
            className={className}
            data-autotest={dataAutotest}
            onAction={(key) => {
                if (key === TOGGLE_ITEM_ID) {
                    if (archive?.isArchived) archive.onRestore();
                    else archive?.onArchive();
                } else if (key === TOGGLE_FACILITY_ARCHIVE_ID) {
                    facilityArchive?.onRestore();
                } else if (key === "delete-row") onDelete();
            }}
        >
            {/* ONE item that swaps its copy, not two that swap places — see the
              * note on TOGGLE_ITEM_ID. Copy is adapted from the facility menu's,
              * with the noun swapped to the row itself (a claim, on Loss Runs). */}
            {showClaimToggle && (
                <AriaMenuItem
                    id={TOGGLE_ITEM_ID}
                    className={ROW_ACTIONS_ITEM_CLASS}
                    textValue={archive!.isArchived ? "Restore" : "Archive"}
                    data-autotest={dataAutotest ? `${dataAutotest}-${archive!.isArchived ? "restore" : "archive"}` : undefined}
                >
                    {archive!.isArchived ? (
                        <RowActionsMenuItemBody
                            icon={RefreshCcw01}
                            title="Restore"
                            description="Restore this claim with its original status and history intact"
                        />
                    ) : (
                        <RowActionsMenuItemBody
                            icon={Folder}
                            title="Archive"
                            description="Remove this claim from active totals while preserving its history and data. It can be restored later."
                        />
                    )}
                </AriaMenuItem>
            )}
            {showFacilityToggle && (
                <AriaMenuItem
                    id={TOGGLE_FACILITY_ARCHIVE_ID}
                    className={ROW_ACTIONS_ITEM_CLASS}
                    textValue="Restore Facility"
                    data-autotest={dataAutotest ? `${dataAutotest}-restore-facility` : undefined}
                >
                    <RowActionsMenuItemBody
                        icon={RefreshCcw01}
                        title="Restore Facility"
                        description={
                            facilityArchive?.alsoRestoresRow
                                ? "Restore this facility and its related data. Also restores this claim, which was archived on its own."
                                : "Restore this facility and its related data across Schedule, Regulatory, Operational, and Legal"
                        }
                    />
                </AriaMenuItem>
            )}
            {(showClaimToggle || showFacilityToggle) && <AriaSeparator className={ROW_ACTIONS_SEPARATOR_CLASS} />}
            <AriaMenuItem
                id="delete-row"
                className={ROW_ACTIONS_ITEM_CLASS}
                textValue={title}
                data-autotest={dataAutotest ? `${dataAutotest}-delete` : undefined}
            >
                <RowActionsMenuItemBody destructive icon={Trash02} title={title} description={description} />
            </AriaMenuItem>
        </RowActionsMenu>
    );
}
